import * as React from "react";
import { DraggerProps } from "antd/lib/upload/Dragger";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { fileUploadStore } from "./FileUploadStore";
import { Button, Row, Col, Upload } from "antd";
import { I18N_NS } from "Config/i18n";
import { LedIndicators, WorkflowLedIndicators } from "./LedIndicators";
import { FileUploadList } from "./FileUploadList";
import { FileUploadChunked } from "./FileUploadChunked";
import { messageInstance } from "App";
import { checkFileAccess } from "utils/checkFileAccess";

interface UploadDraggerProps extends WithTranslation {
  onNext: () => void;
  showPathname?: boolean;
}

export const UploadDragger = withTranslation(I18N_NS.COMPONENT_UPLOAD)(
  observer(
    class extends React.Component<UploadDraggerProps> {
      get isDisabledButton() {
        if (fileUploadStore.module === "VIEW") {
          return (
            !fileUploadStore.filesMap.size || fileUploadStore.getFilesByErrorStatus("VIEW_NO_AUTOMAPPING").length > 0
          );
        }
        return !fileUploadStore.filesMap.size;
      }

      get draggerProps() {
        const uploadURL = API_URL.FILES.checkFiles(projectsDataStore.projectId, fileUploadStore.folderId);
        const httpHeader = API.headersObject();

        const object: DraggerProps = {
          beforeUpload: (file) => FileUploadChunked.fileIsNotTooBig(file),
          customRequest: fileUploadStore.onRequest,
          onRemove: (file) => fileUploadStore.deleteFile(file.uid),
          action: uploadURL,
          showUploadList: false,
          headers: httpHeader,
          multiple: true,
          onDrop: async (e) => {
            // Check all files in the drop event
            const files = e.dataTransfer.files;
            for (let i = 0; i < files.length; i++) {
              const result = await checkFileAccess(files[i]);
              if (!result) {
                messageInstance.error(
                  this.props.t(
                    // eslint-disable-next-line max-len
                    "Cannot access file. It may be locked by another application, or you have problems with your network connection.",
                    { ns: I18N_NS.COMPONENT_UPLOAD }
                  )
                );
                setTimeout(() => {
                  fileUploadStore.deleteFileByFilename(files[i].name);
                }, 400);
                return false;
              }
            }

            const entries = e.dataTransfer.items || e.dataTransfer.files;
            let isDirectory = false;
            for (const item of Array.from<DataTransferItem | File>(entries)) {
              // If dropped items aren't files, reject them
              if (item instanceof File) {
                continue;
              }

              if (item.kind === "file") {
                const entry = item.webkitGetAsEntry?.();
                if (entry === null) {
                  messageInstance.error(this.props.t("File could not be read"));
                  continue;
                }
                if (entry.isDirectory) {
                  isDirectory = true;
                  messageInstance.error(this.props.t("Directory upload is not supported"));
                  // SetTimeout is required because onDrop is a callback,
                  // and by the time it is triggered, the file is not yet added to the store
                  setTimeout(() => {
                    fileUploadStore.deleteFileByFilename((entry as FileSystemEntry).name);
                  }, 400);
                  continue;
                }
              }
            }
            if (isDirectory) {
              return false;
            }
            return e;
          },
        };
        return object;
      }

      render() {
        const { t, onNext } = this.props;
        const uploadMode = fileUploadStore.module;
        return (
          <div className="upload-modal-body">
            <Row className="mb-2" gutter={10}>
              <Col md={15}>
                <Upload.Dragger {...this.draggerProps} style={{ minHeight: "120px" }}>
                  <p className="ant-upload-drag-icon">
                    <FontAwesomeIcon icon={["far", "upload"]} size="2x" />
                  </p>
                  <p className="ant-upload-text">{t("Click or drag file into this area to upload")}</p>
                </Upload.Dragger>
              </Col>
              <Col md={9}>
                {uploadMode === "WORKFLOW" ? (
                  <WorkflowLedIndicators
                    accessRight={fileUploadStore.specificWorkflowOptions.workflowAccess}
                    filenameRulesIsOn={fileUploadStore.specificWorkflowOptions.fileNameRulesEnabled}
                    metadataIsOn={fileUploadStore.specificWorkflowOptions.metadataEnabled}
                  />
                ) : (
                  <LedIndicators functions={projectsDataStore.functions} />
                )}
              </Col>
            </Row>
            <FileUploadList
              pathNameInfo={this.props.showPathname}
              directoriesCreated={fileUploadStore.directoriesCreated}
              files={fileUploadStore.uploadFilesList}
              onExtract={fileUploadStore.zipExtract}
              onRemove={fileUploadStore.deleteFile}
            />
            <div className="text-right mt-1">
              <Button
                type="primary"
                disabled={this.isDisabledButton}
                loading={fileUploadStore.fileUploadNotFinished}
                onClick={onNext}
              >
                {t(`${I18N_NS.PROJECT}:::Next`)}
              </Button>
            </div>
          </div>
        );
      }
    }
  )
);
